"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface BadgeCheckIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface BadgeCheckIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}
const BADGE_VARIANTS: Variants = {
  normal: {
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  animate: {
    scale: [1, 0.9, 1],
    transition: {
      duration: 0.6,
      ease: "easeInOut"
    }
  }
};
const CHECK_VARIANTS: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      duration: 0.6,
      ease: "easeInOut",
      opacity: {
        duration: 0.01
      }
    }
  }
};
const BadgeCheckIcon = forwardRef<BadgeCheckIconHandle, BadgeCheckIconProps>((t0, ref) => {
  const $ = _c(32);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 20 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("inline-flex items-center justify-center", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== size) {
    t6 = {
      width: size,
      height: size
    };
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  let t8;
  if ($[18] !== controls) {
    t7 = <m.path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" variants={BADGE_VARIANTS} initial="normal" animate={controls} />;
    t8 = <m.path d="m9 12 2 2 4-4" variants={CHECK_VARIANTS} initial="normal" animate={controls} />;
    $[18] = controls;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  let t9;
  if ($[21] !== size || $[22] !== t7 || $[23] !== t8) {
    t9 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t7}{t8}</svg>;
    $[21] = size;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== handleMouseEnter || $[26] !== handleMouseLeave || $[27] !== props || $[28] !== t5 || $[29] !== t6 || $[30] !== t9) {
    t10 = <div className={t5} style={t6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t9}</div>;
    $[25] = handleMouseEnter;
    $[26] = handleMouseLeave;
    $[27] = props;
    $[28] = t5;
    $[29] = t6;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
});
BadgeCheckIcon.displayName = "BadgeCheckIcon";
export { BadgeCheckIcon };